import { isEmpty, omit } from 'radash'
import type { HitpayPaymentMethod } from '@/types/payment'
import type {
  GetBookingDetailResponse,
  GetBookingPlanResponse,
  GetBookingTransportationResponse,
  GetListDateTypesResponse,
  GetListDatesResponse,
  GetListTimeResponse,
  GetListTimeSlotResponse,
  GetListTimeTypesResponse,
  GetSelectedDateResponse,
  GetSelectedTimeResponse,
  PatchApplyVoucherPayload,
  PatchBookingHourlyRoutesResponse,
  PatchBookingPassengerParams,
  PatchBookingPassengerResponse,
  PatchBookingPointsRoutesResponse,
  PatchCheckoutBookingPayload,
  PatchCheckoutBookingResponse,
  PostBookingPointsRoutesResponse,
  PostCalculateCancelPayload,
  PostCreateBookingResponse,
  PostSelectBusPlanResponse,
  PutBookingSearchResponse,
  PutClaimBookingPayload,
  PutUpdateBookingResponse,
  SelectBusPlanPayload,
  UpdateSelectedDateResponse,
  UpdateSelectedTimeResponse,
  UpdateTripTypeResponse,
} from '@/types/booking'
import type { Options } from 'ky'
import type {
  CancelBookingFormFieldSchema,
  CancelUnpaidBookingFormFieldSchema,
  CreateBookingTransportationFormFieldSchema,
  GetListTimeSlotSchema,
  GetSelectedTimeSchema,
  ManageBookingFormFieldSchema,
  PatchBookingHourlyRoutesPayloadSchema,
  PatchBookingPointsRoutesPayloadSchema,
  PostBookingAmendPayloadSchema,
  UpdateBookingInformationFormFieldSchema,
  UpdateBookingTransportationFormFieldSchema,
  UpdateSelectedDateSchema,
  UpdateSelectedTimeSchema,
  UpdateTransportationFormFieldSchema,
  UpdateTripTypeSchema,
} from '@/schemas/booking'
import type { PostBookingSubmitAmendPayload } from '@/types/auth'
import type { ValueOf } from 'type-fest'
import type { PAYMENT_METHOD_CODE } from '@/utils/constant'
import { httpClient, httpClientUnauthenticated } from '@/utils/http'

export const bookingService = {
  search: (payload: ManageBookingFormFieldSchema, options?: Options) =>
    httpClient
      .post('bookings/search', {
        ...options,
        json: payload,
        // @ts-expect-error
        strictAuth: false,
      })
      .json<PutBookingSearchResponse>(),
  create: (payload: CreateBookingTransportationFormFieldSchema) =>
    httpClient
      .post('bookings', { json: payload })
      .json<PostCreateBookingResponse>(),
  update: (payload: UpdateBookingTransportationFormFieldSchema) =>
    httpClient
      .put(`bookings/${payload.id}`, { json: omit(payload, ['id']) })
      .json<PutUpdateBookingResponse>(),
  detail: (id: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}/detail`, options)
      .json<GetBookingDetailResponse>(),
  transportation: (id: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}`, options)
      .json<GetBookingTransportationResponse>(),
  updateTransportationPassenger: ({
    id,
    payload,
  }: PatchBookingPassengerParams) =>
    httpClient
      .patch(`bookings/${id}/passenger_quantity`, {
        json: payload,
      })
      .json<PatchBookingPassengerResponse>(),
  deleteTransportation: ({
    bookingId,
    bookedTransportationId,
  }: {
    bookingId: string
    bookedTransportationId: string
  }) =>
    httpClient
      .delete(
        `bookings/${bookingId}/booked_transportation/${bookedTransportationId}`,
      )
      .json(),
  updateTransportation: ({
    id,
    transportation_type_id,
  }: UpdateTransportationFormFieldSchema) =>
    httpClient
      .put(`bookings/${id}/update_bus`, { json: { transportation_type_id } })
      .json<GetBookingTransportationResponse>(),
  updateInfo: (payload: UpdateBookingInformationFormFieldSchema) =>
    httpClient
      .put(`bookings/${payload.id}/update_info`, {
        json: omit(payload, ['id']),
      })
      .json<PutUpdateBookingResponse>(),
  claim: (payload: PutClaimBookingPayload) =>
    httpClientUnauthenticated
      .put(`bookings/${payload.id}/claim_booking`, {
        json: omit(payload, ['id']),
      })
      .json<PutUpdateBookingResponse>(),
  invoice: (
    {
      id,
      method,
      hitpay_payment_method,
    }: {
      id: string
      method: ValueOf<typeof PAYMENT_METHOD_CODE> | null
      hitpay_payment_method?: HitpayPaymentMethod
    },
    options?: Options,
  ) =>
    httpClient
      .get(`invoices/${id}`, {
        ...options,
        timeout: 60_000,
        retry: 0,
        searchParams: {
          // @ts-expect-error
          ...options?.searchParams,
          ...(method ? { payment_method: method } : {}),
          ...(hitpay_payment_method ? { hitpay_payment_method } : {}),
        },
      })
      .blob(),
  quotation: (
    {
      id,
      method,
      hitpay_payment_method,
    }: {
      id: string
      method: ValueOf<typeof PAYMENT_METHOD_CODE> | null
      hitpay_payment_method?: HitpayPaymentMethod
    },
    options?: Options,
  ) =>
    httpClient
      .get(`quotations/${id}`, {
        ...options,
        timeout: 60_000,
        retry: 0,
        searchParams: {
          // @ts-expect-error
          ...options?.searchParams,
          ...(method ? { payment_method: method } : {}),
          ...(hitpay_payment_method ? { hitpay_payment_method } : {}),
        },
      })
      .blob(),
  plan: (options?: Options) =>
    httpClient.get('booking_plans', options).json<GetBookingPlanResponse>(),
  selectPlan: (params: SelectBusPlanPayload, options?: Options) =>
    httpClient
      .post('booking_plans/selected', {
        ...options,
        json: params,
      })
      .json<PostSelectBusPlanResponse>(),
  applyVoucher: (payload: PatchApplyVoucherPayload, options?: Options) =>
    httpClient
      .patch(`bookings/${payload.id}/apply_voucher`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<GetBookingDetailResponse>(),
  deleteVoucher: (payload: PatchApplyVoucherPayload, options?: Options) =>
    httpClient
      .delete(`bookings/${payload.id}/remove_voucher`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<GetBookingDetailResponse>(),
  checkout: (payload: PatchCheckoutBookingPayload, options?: Options) =>
    httpClient
      .patch(`checkouts/${payload.id}`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<PatchCheckoutBookingResponse>(),
  cancel: (payload: CancelBookingFormFieldSchema, options?: Options) =>
    httpClient
      .patch(`bookings/${payload.id}/cancel`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<GetBookingDetailResponse>(),
  cancelUnpaid: (
    payload: CancelUnpaidBookingFormFieldSchema,
    options?: Options,
  ) =>
    httpClient
      .patch(`bookings/${payload.id}/cancel_unpaid`, {
        ...options,
        ...(!isEmpty(omit(payload, ['id']))
          ? { json: omit(payload, ['id']) }
          : {}),
      })
      .json<GetBookingDetailResponse>(),
  calculateCancel: (payload: PostCalculateCancelPayload, options?: Options) =>
    httpClient
      .post(`bookings/${payload.id}/cancel`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<GetBookingDetailResponse>(),
  updateTransportationTripType: ({ id, payload }: UpdateTripTypeSchema) =>
    httpClient
      .patch(`bookings/${id}/trip_type`, { json: payload })
      .json<UpdateTripTypeResponse>(),
  getListDates: (id: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}/dates`, options)
      .json<GetListDatesResponse>(),
  getListDateTypes: (id: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}/dates/types`, options)
      .json<GetListDateTypesResponse>(),
  getSelectedDate: (id: string, date: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}/dates/${date}`, options)
      .json<GetSelectedDateResponse>(),
  updateTransportationSelectedDate: ({ id, date }: UpdateSelectedDateSchema) =>
    httpClient
      .patch(`bookings/${id}/dates/${date}/select`)
      .json<UpdateSelectedDateResponse>(),
  getListTime: (id: string, options?: Options) =>
    httpClient.get(`bookings/${id}/times`, options).json<GetListTimeResponse>(),
  getListTimeTypes: (id: string, options?: Options) =>
    httpClient
      .get(`bookings/${id}/times/types`, options)
      .json<GetListTimeTypesResponse>(),
  getListTimeSlot: (
    { id, time_slot }: GetListTimeSlotSchema,
    options?: Options,
  ) =>
    httpClient
      .get(`bookings/${id}/times/${time_slot}`, options)
      .json<GetListTimeSlotResponse>(),
  updateTransportationSelectedTime: ({
    id,
    time_slot,
    ...body
  }: UpdateSelectedTimeSchema) =>
    httpClient
      .patch(`bookings/${id}/times/${time_slot}/select`, { json: body })
      .json<UpdateSelectedTimeResponse>(),
  getTransportationSelectedTime: (
    { id, time_slot, ...search }: GetSelectedTimeSchema,
    options?: Options,
  ) =>
    httpClient
      .get(`bookings/${id}/times/${time_slot}/select`, {
        ...options,
        // @ts-expect-error
        searchParams: { ...search, ...(options?.searchParams ?? {}) },
      })
      .json<GetSelectedTimeResponse>(),
  postBookingPointsRoutes: (
    {
      id,
      payload,
    }: {
      id: string
      payload: PatchBookingPointsRoutesPayloadSchema
    },
    options?: Options,
  ) =>
    httpClient
      .post(`bookings/${id}/points/routes`, {
        json: payload,
        ...options,
      })
      .json<PostBookingPointsRoutesResponse>(),
  patchBookingPointsRoutes: ({
    id,
    payload,
  }: {
    id: string
    payload: PatchBookingPointsRoutesPayloadSchema
  }) =>
    httpClient
      .patch(`bookings/${id}/points/routes`, {
        json: payload,
      })
      .json<PatchBookingPointsRoutesResponse>(),
  patchBookingHourlyRoutes: ({
    id,
    payload,
  }: {
    id: string
    payload: PatchBookingHourlyRoutesPayloadSchema
  }) =>
    httpClient
      .patch(`bookings/${id}/hourly/routes`, {
        json: payload,
      })
      .json<PatchBookingHourlyRoutesResponse>(),
  amend: (payload: PostBookingAmendPayloadSchema, options?: Options) =>
    httpClient
      .post(`bookings/${payload.id}/amend`, {
        ...options,
        json: omit(payload, ['id']),
      })
      .json<GetBookingDetailResponse>(),
  submitAmend: (payload: PostBookingSubmitAmendPayload, options?: Options) =>
    httpClient
      .post(`bookings/${payload.id}/amends/submit`, {
        ...options,
        ...(payload.contact_email
          ? {
              searchParams: {
                contact_email: payload.contact_email,
              },
            }
          : {}),
      })
      .json<GetBookingDetailResponse>(),
}
